import * as React from 'react';
import { View } from 'react-native';

import type * as api from '@gitbook/api-client';
import type { PaletteCommandDef, PaletteGroupDef } from '@gitbook/react-uikit';

import { type ConfirmWrapCallback, useContentKitConfirm } from './ContentKitConfirm';
import { getContentKitIconComponent } from './ContentKitIcon';
import { ensureStackElements } from './ContentKitStack';
import type { ContentKitDispatchActionFn, ContentKitElementProps } from './types';

/**
 * Render a custom block element.
 */
export function ContentKitBlock(
    props: ContentKitElementProps<api.ContentKitBlock>
): React.ReactElement {
    const { element, renderElements } = props;

    return <View>{renderElements('block', ensureStackElements('vstack', element.children))}</View>;
}

/**
 * Compute the command palette for the block controls.
 */
export function useContentKitBlockControls(
    dispatchAction: ContentKitDispatchActionFn
): [(element: api.ContentKitBlock) => PaletteGroupDef[], React.ReactElement | null] {
    const [confirm, wrapConfirm] = useContentKitConfirm();

    const computeGroups = React.useCallback(
        (element) => {
            return createPaletteGroupsFromControls(
                element.controls || [],
                dispatchAction,
                wrapConfirm
            );
        },
        [dispatchAction, wrapConfirm]
    );

    return [computeGroups, confirm];
}

function createPaletteGroupsFromControls(
    controls: Array<api.ContentKitBlockControl[] | api.ContentKitBlockControl>,
    dispatchAction: ContentKitDispatchActionFn,
    wrapConfirm: ConfirmWrapCallback,
    parentGroupKey = ''
): PaletteGroupDef[] {
    const groups: PaletteGroupDef[] = [];

    let pending: PaletteGroupDef | undefined;

    controls.forEach((control, index) => {
        if (Array.isArray(control)) {
            if (pending) {
                groups.push(pending);
                pending = undefined;
            }

            const subgroups = createPaletteGroupsFromControls(
                control,
                dispatchAction,
                wrapConfirm,
                `group-${index}`
            );
            groups.push(...subgroups);
        } else {
            const command = createPaletteCommandFromControl(
                `${parentGroupKey}-command-${index}`,
                control,
                dispatchAction,
                wrapConfirm
            );

            if (pending) {
                // @ts-ignore - we know that we are using an array of commands
                pending.commands.push(command);
            } else {
                pending = {
                    key: `group-${index}`,
                    commands: [command],
                };
            }
        }
    });

    if (pending) {
        groups.push(pending);
        pending = undefined;
    }

    return groups;
}

function createPaletteCommandFromControl(
    key: string,
    control: api.ContentKitBlockControl,
    dispatchAction: ContentKitDispatchActionFn,
    wrapConfirm: ConfirmWrapCallback
): PaletteCommandDef {
    return {
        key,
        title: control.label,
        icon: control.icon ? getContentKitIconComponent(control.icon) : undefined,
        trigger: () => {
            if (control.confirm) {
                wrapConfirm(control.confirm, async () => {
                    return dispatchAction(control.onPress);
                });
                return;
            }

            return dispatchAction(control.onPress);
        },
    };
}
